/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useRef, useState } from 'react';
import { Button, Image, Modal } from 'react-bootstrap';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  camelCase, compact, isEmpty, startCase,
} from 'lodash-es';
import PropTypes from 'prop-types';
import validator from 'validator';
import XLSX from 'xlsx-js-style';

import IndividualListExample from '../../../../assets/itpmetrics/individual-survey-participant-list-example.png';
import TeamListExample from '../../../../assets/itpmetrics/team-survey-participant-list-example.png';
import { addRespondents, getRandomNames } from '../../../../bridge';
import { isIndividualTemplateType } from '../../../../helpers/itpmetricsUtils';
import { logError } from '../../../../helpers/logger';
import { __ } from '../../../../helpers/translations';

const renameMappings = {
  group: 'team',
  givenName: 'firstName',
};

function UploadParticipantListModal({
  surveyId, templateType, onCancel, onChanged,
}) {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [list, setList] = useState([]);
  const [missingFieldsMessage, setMissingFieldsMessage] = useState(null);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setMissingFieldsMessage(null);
    setError(null);

    try {
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsArrayBuffer(file);
      });

      const data = new Uint8Array(arrayBuffer);

      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      const camelCasedData = jsonData.map((obj) => {
        const entry = {};
        Object.keys(obj).forEach((key) => {
          const camelKey = camelCase(key);
          if (renameMappings[camelKey]) {
            entry[renameMappings[camelKey]] = String(obj[key]).trim();
          } else {
            entry[camelKey] = String(obj[key]).trim();
          }
        });
        Object.keys(entry).forEach((key) => {
          if (!entry[key]) {
            delete entry[key];
          }
        });
        if (isEmpty(entry)) {
          return null;
        }
        return entry;
      });
      console.log('camelCasedData', camelCasedData);

      setList(compact(camelCasedData));
    } catch (e) {
      logError('upload_participant_list_modal_on_file_change', e);
    }
  };

  const onClickUpload = async () => {
    try {
      if (!selectedFile || !selectedFile.name) {
        setSelectedFile(null);
        return;
      }

      const requiredFields = ['firstName', 'lastName', 'email', 'team'];
      if (isIndividualTemplateType(templateType)) {
        requiredFields.splice(requiredFields.length - 1, 1);
      }
      const missingFields = requiredFields.filter(
        (field) => !list[0] || !Object.keys(list[0]).includes(field),
      );

      if (missingFields.length > 0) {
        const missingFieldsFormatted = missingFields.map((field) => startCase(field)).join(', ');
        setMissingFieldsMessage(__('G123', null, [missingFieldsFormatted]));
        return;
      }
      console.log(list);
      const invalidEmails = list.filter(({ email }) => !validator.isEmail(email))
        .map(({ email }) => email);
      console.log(invalidEmails);
      if (invalidEmails.length > 0) {
        setError(__('G124', null, [invalidEmails.join(' ')]));
        return;
      }

      const missingCells = list.filter(
        (entry) => requiredFields.find(
          (field) => !entry[field] || String(entry[field]).trim().length === 0,
        ),
      );

      if (missingCells.length > 0) {
        const cells = Object.values(missingCells[0]).map((cell) => cell.trim());
        setError(__('G125', null, [compact(cells).join(', ')]));
        return;
      }

      const cleanedList = list.map(({
        firstName, lastName, email, team,
      }) => ({
        firstName: String(firstName).trim(),
        lastName: String(lastName).trim(),
        email: String(email).trim().toLowerCase(),
        team: team ? String(team).trim() : null,
      }));

      setIsUploading(true);
      const { error: err } = await addRespondents(surveyId, cleanedList);
      setIsUploading(false);
      if (err) {
        setError(`${err.message}: ${err.emails.join(', ')}`);
        return;
      }
      onChanged();
    } catch (err) {
      setIsUploading(false);
      logError('upload_participant_list_modal_on_click_upload', err);
    }
  };

  const onClickChooseFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onClickCancel = () => {
    setSelectedFile({});
    setList([]);
    onCancel();
  };

  const onClickUseDemoList = async () => {
    const { response: names } = await getRandomNames(20);
    await addRespondents(surveyId, names);
    onChanged();
  };

  return (
    <Modal show centered onHide={onCancel}>
      <Modal.Header>
        <Modal.Title>{__('G122')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{__('G126')}</p>
        <p>
          {__('G127')}
          {' '}
          <a
            style={{ cursor: 'pointer', textDecorationLine: 'underline' }}
            onClick={onClickUseDemoList}
          >
            {__('G128')}
          </a>
        </p>
        {isIndividualTemplateType(templateType)
          ? <Image src={IndividualListExample} className="img-fluid w-50" />
          : <Image src={TeamListExample} className="img-fluid w-50" />}
        <br />
        <br />
        <Button onClick={onClickChooseFile} variant="outline-primary">
          <FontAwesomeIcon icon={faUpload} />
          {' '}
          {__('G129')}
        </Button>
        <br />
        <input
          type="file"
          id="fileInput"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".xls, .xlsx, .csv"
          onChange={onFileChange}
        />
        {selectedFile && (
        <small>{selectedFile.name}</small>
        )}

        {!selectedFile && <div className="text-danger fw-bold">{__('G130')}</div>}
        {missingFieldsMessage && <div className="text-danger fw-bold">{missingFieldsMessage}</div>}
        {error && <div className="text-danger fw-bold">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClickCancel}>
          {__('G106')}
        </Button>
        <Button variant="primary" onClick={onClickUpload} disabled={isUploading}>
          {isUploading ? __('G52') : __('G107')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

UploadParticipantListModal.propTypes = {
  surveyId: PropTypes.string,
  templateType: PropTypes.string,
  onCancel: PropTypes.func,
  onChanged: PropTypes.func,
};

export default UploadParticipantListModal;
